import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { fmtIDR, apiErr, isInternal } from "@/lib/api";
import { DateInput, fmtDate } from "@/components/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { LedgerInput } from "@/components/LedgerInput";
import { PurchaseItems, blankItem } from "@/components/txn/PurchaseItems";
import { InvoiceLinkInput } from "@/components/txn/InvoiceLinkInput";
import { JournalInline } from "@/components/txn/JournalInline";
import { TallyLines, isBlankLine } from "@/components/TallyLines";
import { AmountInput, fmtAmount, fmtRp } from "@/components/AmountInput";
import { tx } from "@/lib/txnI18n";
import { taxLabel, isPpn, isPph } from "@/lib/masterI18n";
import { fl } from "@/lib/finI18n";
import { KopSelect } from "@/components/txn/KopSelect";
import { TaxIdReminder } from "@/components/txn/TaxIdReminder";
import { usePayables, autoSettle, PayableNotice } from "@/components/txn/PayableSync";
import { toast } from "sonner";

const MODES = ["out", "in", "journal", "contra", "purchase", "sales"];
const txnLine = (dc) => ({ dc, ledger: "", coa_id: "", amount: "", note: "" });
const sideOf = (mode) => (mode === "in" ? "cr" : "dr");
// Net DPP of Keluar / Masuk rows: the main side (Dr for Keluar, Cr for Masuk) minus the other side.
const netOf = (lines, mode) => Math.round(lines.reduce((s, l) => s + (l.dc === sideOf(mode) ? 1 : -1) * (Number(l.amount) || 0), 0) * 100) / 100;

// Contra between two currencies: received amount in the target currency; both sides at KMK, the gap is FX gain/loss.
function ContraFx({ cur, toCur, value, onChange, amountOrig, idrBase, toRate, toIdr, fxDiff, toKmk, lang }) {
  const got = Number(value) || 0;
  const eff = !amountOrig || !got ? null : cur === "IDR" ? amountOrig / got : toCur === "IDR" ? got / amountOrig : got / amountOrig;
  const n2 = (v) => fmtAmount(v, lang);
  const effLbl = cur === "IDR" ? `1 ${toCur} = Rp ${n2(eff)}` : toCur === "IDR" ? `1 ${cur} = Rp ${n2(eff)}` : `1 ${cur} = ${n2(eff)} ${toCur}`;
  return (
    <div className="space-y-1.5 sm:col-span-2 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="txn-contra-fx">
      <Label>{tx("gotAmt", lang)} ({toCur})</Label>
      <AmountInput value={value} onChange={onChange} placeholder="0" className="num" data-testid="txn-to-amount" />
      {toCur !== "IDR" && <p className="text-[11px] text-muted-foreground">{tx("kmkRate", lang)} {toCur}: {toKmk?.rate ? `Rp ${n2(toRate)} · KMK ${toKmk.kmk_no}` : tx(toKmk?.error ? "notYet" : "loading", lang)}</p>}
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px]">
        <span className="text-muted-foreground">{tx("sentKmk", lang)}</span><span className="num text-right">{fmtRp(idrBase, lang)}</span>
        <span className="text-muted-foreground">{tx("gotKmk", lang)}</span><span className="num text-right">{fmtRp(toIdr, lang)}</span>
        {eff && <><span className="text-muted-foreground">{tx("effRate", lang)}</span><span className="num text-right">{effLbl}</span></>}
        <span className="font-medium">{tx(fxDiff >= 0 ? "fxGain" : "fxLoss", lang)}</span>
        <span className={`num text-right font-semibold ${fxDiff >= 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid="txn-fx-diff">{fmtRp(Math.abs(fxDiff), lang)}</span>
      </div>
      <p className="text-[10px] text-muted-foreground">{tx("fxNote", lang)}</p>
    </div>
  );
}
const empty = () => ({
  mode: "out", date: new Date().toISOString().slice(0, 10),
  bank_account_id: "", invoice_no: "", invoice_id: "", vendor: "", description: "",
  amount: "", to_bank_id: "", to_amount: "", coa_id: "", project_id: "none", tax_type_id: "none",
  ppn_type_id: "none", ppn_amount: 0, pph_type_id: "none", pph_amount: 0, kop: "",
  tax_rate: 0, pending_document: false, fx_rate: 1, items: [blankItem()], balance_due: null, lines: [txnLine("dr")],
});
const modeOf = (tx) => (tx.kind === "contra" ? "contra" : tx.kind === "purchase" ? "purchase"
  : (tx.kind === "sales" || tx.source === "invoice_payment") ? "sales" : tx.flow === "in" ? "in" : "out");
const itemsTotal = (items) => Math.round(items.reduce((s, it) => s + (Number(it.total) || 0), 0) * 100) / 100;

function KmkHint({ kmk, manual, lang, onUse }) {
  if (manual) {
    return (
      <p className="text-[11px] text-amber-700 flex items-center gap-2" data-testid="txn-fx-manual">{fl("kmkManual", lang)}
        <button type="button" className="font-medium text-primary hover:underline" onClick={onUse} data-testid="txn-fx-use-kmk">{fl("kmkUse", lang)}</button></p>
    );
  }
  if (!kmk) return null;
  if (kmk.error) return <p className="text-[11px] text-rose-600" data-testid="txn-fx-kmk-missing">{fl("kmkMissing", lang)}</p>;
  return (
    <p className="text-[11px] text-emerald-700" data-testid="txn-fx-kmk">KMK {kmk.kmk_no} · {fl("kmkValid", lang)} {fmtDate(kmk.start)}–{fmtDate(kmk.end)}{kmk.fallback ? ` (${fl("kmkFallback", lang)})` : ""}</p>
  );
}

export function TransactionForm({ open, onOpenChange, clientId, editTxn, onSaved }) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [form, setForm] = useState(empty);
  const [coa, setCoa] = useState([]);
  const [banks, setBanks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [attachFile, setAttachFile] = useState(null);
  const [keepOpen, setKeepOpen] = useState(true);
  const [fileKey, setFileKey] = useState(0);
  const [kmk, setKmk] = useState(null);

  useEffect(() => {
    if (!open || !clientId) return;
    Promise.all([
      api.get(`/coa?client_id=${clientId}`), api.get(`/banks?client_id=${clientId}`),
      api.get(`/projects?client_id=${clientId}`), api.get(`/taxes?client_id=${clientId}`),
    ]).then(([c, b, p, tx]) => { setCoa(c.data); setBanks(b.data); setProjects(p.data); setTaxes(tx.data); });
  }, [open, clientId]);

  useEffect(() => {
    if (!editTxn) { setForm(empty()); return; }
    setForm({
      ...empty(), mode: modeOf(editTxn), date: editTxn.date, bank_account_id: editTxn.bank_account_id, invoice_no: editTxn.invoice_no || "",
      invoice_id: editTxn.invoice_id || "", vendor: editTxn.vendor || "", description: editTxn.description || "",
      amount: (editTxn.amount_original ?? editTxn.amount), to_bank_id: editTxn.to_bank_id || "", to_amount: editTxn.to_amount ?? "",
      coa_id: editTxn.coa_id || "", project_id: editTxn.project_id || "none",
      tax_type_id: editTxn.tax_type_id || "none", tax_rate: editTxn.tax_rate || 0,
      ppn_type_id: editTxn.ppn_type_id || "none", ppn_amount: editTxn.ppn_amount || 0,
      pph_type_id: editTxn.pph_type_id || "none", pph_amount: editTxn.pph_amount || 0, kop: editTxn.kop || "",
      pending_document: editTxn.pending_document || false, fx_rate: editTxn.fx_rate || 1, fx_source: editTxn.fx_source || "",
      items: editTxn.items?.length ? editTxn.items.map((it) => ({ ...it, qty: String(it.qty), unit_price: String(it.unit_price), total: String(it.total) })) : [blankItem()],
      lines: editTxn.lines?.length ? editTxn.lines.map((l) => ({ dc: l.dc, ledger: l.ledger || "", coa_id: l.coa_id, amount: String(l.amount), note: l.note || "", keep: "loaded" }))
        : [{ dc: editTxn.flow === "in" ? "cr" : "dr", ledger: editTxn.vendor || "", coa_id: editTxn.coa_id || "", amount: String(editTxn.amount_original ?? editTxn.amount ?? ""), note: "", keep: "loaded" }],
    });
  }, [editTxn, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const mode = form.mode;
  const isPurchase = mode === "purchase";
  const isSales = mode === "sales";
  const isLines = mode === "out" || mode === "in";
  const full = isLines ? "sm:col-span-2 lg:col-span-4" : "sm:col-span-2";
  const partyLine = form.lines.filter((l) => !isBlankLine(l)).find((l) => l.dc === sideOf(mode)) || form.lines.find((l) => !isBlankLine(l));
  const partyName = ((isLines ? partyLine?.ledger : form.vendor) || "").trim();
  const selBank = banks.find((b) => b.id === form.bank_account_id);
  const cur = isSales ? "IDR" : (selBank?.currency || "IDR");
  const isFx = cur !== "IDR";
  const rate = isFx ? (Number(form.fx_rate) || 0) : 1;
  const amountOrig = isPurchase ? itemsTotal(form.items) : isLines ? netOf(form.lines, mode) : (Number(form.amount) || 0);
  const idrBase = amountOrig * rate;
  const toBank = banks.find((b) => b.id === form.to_bank_id);
  const toCur = toBank?.currency || "IDR";
  const cross = mode === "contra" && !!toBank && !!selBank && toCur !== cur;
  const [toKmk, setToKmk] = useState(null);
  useEffect(() => {
    if (!open || !cross || toCur === "IDR" || !/^\d{4}-\d{2}-\d{2}$/.test(form.date || "")) { setToKmk(null); return undefined; }
    let off = false;
    api.get(`/kurs/kmk/rate?currency=${toCur}&date=${form.date}`).then(({ data }) => { if (!off) setToKmk(data); }).catch(() => { if (!off) setToKmk({ error: true }); });
    return () => { off = true; };
  }, [open, cross, toCur, form.date]);
  const toRate = toCur === "IDR" ? 1 : (toKmk?.rate || 0);
  const toIdr = (Number(form.to_amount) || 0) * toRate;
  const fxDiff = cross ? toIdr - idrBase : 0;

  const withTax = (f, base) => {
    const pt = taxes.find((x) => x.id === f.ppn_type_id);
    const ht = taxes.find((x) => x.id === f.pph_type_id);
    return { ...f, ppn_amount: pt ? Math.round(base * pt.rate / 100) : f.ppn_amount, pph_amount: ht ? Math.round(base * ht.rate / 100) : f.pph_amount };
  };
  const onAmount = (v) => setForm((f) => withTax({ ...f, amount: v }, (Number(v) || 0) * rate));
  const baseOf = (f) => (f.mode === "purchase" ? itemsTotal(f.items) : (f.mode === "out" || f.mode === "in") ? netOf(f.lines, f.mode) : Number(f.amount) || 0);
  const onRate = (v, src = "manual") => setForm((f) => withTax({ ...f, fx_rate: v, fx_source: src }, baseOf(f) * (Number(v) || 0)));
  const onLines = (lines) => setForm((f) => withTax({ ...f, lines }, netOf(lines, f.mode) * rate));
  // Rows edited by hand drop their auto-settlement mark, so picking an accrued expense again re-applies it.
  const onLinesUser = (lines) => setForm((f) => withTax({ ...f, lines: lines.map((l, i) => (l.auto && f.lines[i] && l.coa_id !== f.lines[i].coa_id ? { ...l, auto: undefined } : l)) },
    netOf(lines, f.mode) * rate));
  const [payN, setPayN] = useState(0);
  const pay = usePayables(open && mode === "out", clientId, form.date, editTxn?.id, lang, payN);
  const withholding = form.pph_type_id !== "none" && (Number(form.pph_amount) || 0) > 0;
  useEffect(() => {
    if (mode !== "out" || !pay || !autoSettle(form.lines, pay, form.description, withholding, rate)) return;
    setForm((f) => { const n = autoSettle(f.lines, pay, f.description, withholding, rate); return n ? { ...f, lines: n } : f; });
  }, [pay, form.lines, form.description, withholding, mode, rate]);
  const autoKmk = isFx && form.fx_source !== "manual";
  useEffect(() => {
    if (!open || !autoKmk || !/^\d{4}-\d{2}-\d{2}$/.test(form.date || "")) { if (!isFx) setKmk(null); return undefined; }
    let off = false;
    api.get(`/kurs/kmk/rate?currency=${cur}&date=${form.date}`)
      .then(({ data }) => { if (!off) { setKmk(data); onRate(String(data.rate), "kmk"); } })
      .catch(() => { if (!off) setKmk({ error: true }); });
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoKmk, isFx, cur, form.date]);
  const onItems = (items) => setForm((f) => withTax({ ...f, items }, itemsTotal(items) * rate));
  const onPPN = (v) => { const x = taxes.find((tx) => tx.id === v); setForm((f) => ({ ...f, ppn_type_id: v, ppn_amount: x ? Math.round(idrBase * x.rate / 100) : 0 })); };
  const onPPH = (v) => { const x = taxes.find((tx) => tx.id === v); setForm((f) => ({ ...f, pph_type_id: v, kop: "", pph_amount: x ? Math.round(idrBase * x.rate / 100) : 0 })); };
  const onMode = (m) => setForm((f) => {
    const flip = (f.mode === "out" && m === "in") || (f.mode === "in" && m === "out");
    return { ...f, mode: m, ...(flip ? { lines: f.lines.map((l) => ({ ...l, dc: l.dc === "dr" ? "cr" : "dr" })) } : {}),
      ...(m === "sales" ? { ppn_type_id: "none", ppn_amount: 0, pph_type_id: "none", pph_amount: 0 } : {}) };
  });
  const onPickInvoice = (d) => {
    const own = editTxn && editTxn.invoice_id === d.id ? Number(editTxn.amount || 0) : 0;
    const due = Math.round((Number(d.balance_due || 0) + own) * 100) / 100;
    setForm((f) => ({ ...f, invoice_id: d.id, invoice_no: d.invoice_no, vendor: d.bill_to?.name || "", amount: due, balance_due: due,
      bank_account_id: f.bank_account_id || d.bank_account_id || "" }));
  };

  const validate = () => {
    if (!form.bank_account_id) return t("tf.pickAccount");
    if (mode === "contra") {
      if (!form.to_bank_id || !form.amount) return "Lengkapi akun asal, tujuan, dan nominal";
      return cross && !(Number(form.to_amount) > 0) ? `Isi jumlah yang diterima di rekening tujuan (${toCur})` : null;
    }
    if (isPurchase) {
      if (form.items.some((it) => !it.name.trim() || !(Number(it.qty) > 0))) return "Lengkapi nama barang & qty setiap baris";
      return itemsTotal(form.items) > 0 ? null : "Total pembelian harus lebih dari 0";
    }
    if (isSales) return !form.invoice_no || !(Number(form.amount) > 0) ? "Pilih nomor invoice & isi nominal penerimaan" : null;
    const filled = form.lines.filter((l) => !isBlankLine(l));
    if (!filled.length) return fl("tlNeedLine", lang);
    if (filled.some((l) => !l.coa_id || !(Number(l.amount) > 0))) return fl("tlLineErr", lang);
    return netOf(filled, mode) > 0 ? null : fl("tlNetErr", lang);
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    const kind = { contra: "contra", purchase: "purchase", sales: "sales" }[mode] || "standard";
    const ppn = isSales ? 0 : Number(form.ppn_amount) || 0;
    const pph = isSales ? 0 : Number(form.pph_amount) || 0;
    const filled = form.lines.filter((l) => !isBlankLine(l));
    const first = filled.find((l) => l.dc === sideOf(mode)) || filled[0];
    const std = kind === "standard";
    const payload = {
      client_id: clientId, date: form.date, bank_account_id: form.bank_account_id,
      invoice_no: form.invoice_no, invoice_id: isSales ? form.invoice_id || null : null,
      vendor: std ? (first?.ledger || "").trim() : form.vendor, description: form.description,
      lines: std ? filled.map((l) => ({ dc: l.dc, ledger: (l.ledger || "").trim(), coa_id: l.coa_id, amount: Number(l.amount), note: (l.note || "").trim() })) : null,
      amount: idrBase, amount_original: amountOrig, currency: cur, fx_rate: rate, fx_source: isFx ? (cross ? "kmk" : form.fx_source || "kmk") : null,
      flow: mode === "in" || isSales ? "in" : "out", kind,
      to_bank_id: kind === "contra" ? form.to_bank_id : null,
      to_amount: kind === "contra" && cross ? Number(form.to_amount) : null,
      coa_id: std ? first?.coa_id : null,
      project_id: form.project_id === "none" ? null : form.project_id,
      tax_type_id: form.tax_type_id === "none" ? null : form.tax_type_id,
      tax_rate: Number(form.tax_rate) || 0, pending_document: form.pending_document,
      ppn_type_id: isSales || form.ppn_type_id === "none" ? null : form.ppn_type_id, ppn_amount: ppn,
      pph_type_id: isSales || form.pph_type_id === "none" ? null : form.pph_type_id, pph_amount: pph,
      kop: isSales || form.pph_type_id === "none" ? "" : form.kop || "",
      total: idrBase + ppn - pph,
      items: isPurchase ? form.items.map((it) => ({ product_id: it.product_id || "", name: it.name.trim(), unit: it.unit, category: it.category,
        qty: Number(it.qty), unit_price: Number(it.unit_price || 0), total: Number(it.total || 0) })) : null,
    };
    try {
      if (editTxn) {
        const { data } = await api.put(`/transactions/${editTxn.id}`, payload);
        toast.success("Transaksi diperbarui");
        if (data.recalculated) toast.info(`${data.recalculated} ${fl("recalcToast", lang)}`);
        setAttachFile(null);
        if (onSaved) onSaved();
        onOpenChange(false);
      } else {
        const { data } = await api.post("/transactions", payload);
        if (attachFile) {
          const fd = new FormData();
          fd.append("file", attachFile);
          try { await api.post(`/transactions/${data.transaction.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch (er) { toast.error("Transaksi tersimpan, namun lampiran gagal diunggah."); }
        }
        if (data.warning) toast.warning(data.warning, { duration: 6000 });
        if (data.recalculated) toast.info(`${data.recalculated} ${fl("recalcToast", lang)}`);
        setAttachFile(null);
        setFileKey((k) => k + 1);
        if (isPurchase || isSales) setForm((f) => ({ ...f, items: [blankItem()], invoice_id: "", invoice_no: "", amount: "", balance_due: null, ...(isSales ? { vendor: "" } : {}) }));
        if (onSaved) onSaved();
        setPayN((n) => n + 1);
        if (keepOpen) toast.success(t("tf.savedKept"));
        else { toast.success("Transaksi dibuat"); onOpenChange(false); }
      }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onKey = (e) => { if (mode !== "journal" && (e.ctrlKey || e.metaKey) && e.key === "Enter") submit(); };
  const modes = MODES.filter((m) => m !== "journal" || (isInternal(user.role) && !editTxn));
  const wide = isPurchase || mode === "journal";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${isLines ? "max-w-6xl" : wide ? "max-w-5xl" : "max-w-2xl"} max-h-[92vh] overflow-y-auto`} onKeyDown={onKey}>
        <DialogHeader>
          <DialogTitle className="font-head">{editTxn ? t("tf.editTitle") : t("tf.newTitle")}</DialogTitle>
        </DialogHeader>
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${isLines ? "lg:grid-cols-4" : ""} gap-4 py-2`}>
          <div className="space-y-1.5">
            <Label>{fl("txnType", lang)}</Label>
            <Select value={mode} onValueChange={onMode}>
              <SelectTrigger data-testid="txn-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{modes.map((m) => <SelectItem key={m} value={m} data-testid={`txn-mode-${m}`}>{fl(`m_${m}`, lang)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {mode === "journal" ? (
            <JournalInline clientId={clientId} coa={coa} onCancel={() => onOpenChange(false)} onSaved={() => { if (onSaved) onSaved(); if (!keepOpen) onOpenChange(false); }} />
          ) : (
            <>
              <div className="space-y-1.5">
                <Label>{t("tf.date")}</Label>
                <DateInput value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="txn-date" />
              </div>
              <div className="space-y-1.5">
                <Label>{mode === "contra" ? t("tf.fromBank") : isSales ? fl("payBank", lang) : t("tf.bank")}</Label>
                <Select value={form.bank_account_id} onValueChange={(v) => set("bank_account_id", v)}>
                  <SelectTrigger data-testid="txn-bank"><SelectValue placeholder={t("tf.pickAccount")} /></SelectTrigger>
                  <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.code ? `${b.code} · ${b.name}` : b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {mode === "contra" && (
                <div className="space-y-1.5">
                  <Label>{t("tf.toBank")}</Label>
                  <Select value={form.to_bank_id} onValueChange={(v) => setForm((f) => ({ ...f, to_bank_id: v, fx_source: f.fx_source === "manual" ? "kmk" : f.fx_source }))}>
                    <SelectTrigger data-testid="txn-tobank"><SelectValue placeholder={t("tf.pickTarget")} /></SelectTrigger>
                    <SelectContent>{banks.filter((b) => b.id !== form.bank_account_id).map((b) => <SelectItem key={b.id} value={b.id}>{b.code ? `${b.code} · ${b.name}` : b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {cross && (
                <ContraFx cur={cur} toCur={toCur} value={form.to_amount} onChange={(v) => set("to_amount", v)} amountOrig={amountOrig} idrBase={idrBase}
                  toRate={toRate} toIdr={toIdr} fxDiff={fxDiff} toKmk={toKmk} lang={lang} />
              )}
              {isSales ? (
                <>
                  <div className="space-y-1.5">
                    <Label>{fl("salesInvoice", lang)}</Label>
                    <InvoiceLinkInput clientId={clientId} value={form.invoice_no} currentId={editTxn?.invoice_id} onPick={onPickInvoice}
                      onChange={(v) => setForm((f) => ({ ...f, invoice_no: v, invoice_id: "", balance_due: null }))} />
                    <p className="text-[11px] text-muted-foreground">{fl("salesHint", lang)}</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{fl("customer", lang)}</Label>
                    <Input value={form.vendor} readOnly className="bg-secondary/40" data-testid="txn-sales-customer" />
                    {form.balance_due != null && <p className="text-[11px] text-muted-foreground" data-testid="txn-sales-balance">{fl("balanceDue", lang)}: {fmtIDR(form.balance_due)}</p>}
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label>{t("tf.invoice")}</Label>
                    <Input value={form.invoice_no} onChange={(e) => set("invoice_no", e.target.value)} data-testid="txn-invoice" placeholder="INV-001" />
                  </div>
                  {mode !== "contra" && !isLines && (
                    <div className="space-y-1.5">
                      <Label>{t("tf.vendor")} / {fl("ledger", lang)}</Label>
                      <LedgerInput clientId={clientId} value={form.vendor} placeholder="PT ..." onChange={(v) => set("vendor", v)}
                        onPick={(l) => setForm((f) => ({ ...f, vendor: l.name, coa_id: (f.mode === "out" || f.mode === "in") ? (l.coa_id || f.coa_id) : f.coa_id }))} />
                      <p className="text-[11px] text-muted-foreground">{fl("ledgerPickHint", lang)}</p>
                    </div>
                  )}
                </>
              )}
              {isPurchase && <PurchaseItems clientId={clientId} items={form.items} onChange={onItems} currency={cur} />}
              {!isLines && (
                <div className="space-y-1.5">
                  <Label>{t("tf.amount")}{isFx ? ` (${cur})` : ""}</Label>
                  <Input type="number" value={isPurchase ? amountOrig : form.amount} readOnly={isPurchase} onChange={(e) => onAmount(e.target.value)}
                    data-testid="txn-amount" placeholder="0" className={`num ${isPurchase ? "bg-secondary/40" : ""}`} />
                  {isSales && <p className="text-[11px] text-muted-foreground">{fl("salesAutoCoa", lang)}</p>}
                </div>
              )}
              {isFx && (
                <div className="space-y-1.5">
                  <Label>{t("tf.rate")} (1 {cur})</Label>
                  <Input type="number" value={form.fx_rate} onChange={(e) => onRate(e.target.value)} readOnly={cross} data-testid="txn-fx-rate" placeholder="0" className={`num ${cross ? "bg-secondary/40" : ""}`} />
                  <KmkHint kmk={kmk} manual={form.fx_source === "manual"} lang={lang} onUse={() => set("fx_source", "kmk")} />
                  <p className="text-[11px] text-muted-foreground">{t("tf.idrEquiv")}: {fmtIDR(idrBase)}</p>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>{t("tf.project")}</Label>
                <Select value={form.project_id} onValueChange={(v) => set("project_id", v)}>
                  <SelectTrigger data-testid="txn-project"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("tf.noProject")}</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isLines && (
                <div className={`${full} space-y-1.5 min-w-0`}>
                  <Label>{fl("tlLinesTitle", lang)}</Label>
                  <TallyLines clientId={clientId} coa={coa} lines={form.lines} onChange={onLinesUser} newLine={() => txnLine(sideOf(mode))} minRows={1}
                    testid="txn" cur={cur} ledgerFillsCoa
                    footer={() => <span className="num font-semibold text-primary" data-testid="txn-net">{fl("tlNet", lang)}: {isFx ? `${fmtAmount(amountOrig, lang)} ${cur}` : `Rp ${fmtAmount(amountOrig, lang)}`}</span>} />
                  {mode === "out" && <PayableNotice lines={form.lines} pay={pay} desc={form.description} withholding={withholding} rate={rate} coa={coa} onLines={onLines} lang={lang} />}
                </div>
              )}
              {!isSales && (
                <>
                  <div className="space-y-1.5">
                    <Label>{t("tf.ppn")}</Label>
                    <Select value={form.ppn_type_id} onValueChange={onPPN}>
                      <SelectTrigger data-testid="txn-ppn"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("tf.noPpn")}</SelectItem>
                        {taxes.filter(isPpn).map((x) => <SelectItem key={x.id} value={x.id}>{taxLabel(x, lang)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("tf.ppnAmount")}</Label>
                    <Input type="number" value={form.ppn_amount} onChange={(e) => set("ppn_amount", e.target.value)} data-testid="txn-ppn-amount" className="num" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("tf.pph")}</Label>
                    <Select value={form.pph_type_id} onValueChange={onPPH}>
                      <SelectTrigger data-testid="txn-pph"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("tf.noPph")}</SelectItem>
                        {taxes.filter(isPph).map((x) => <SelectItem key={x.id} value={x.id}>{taxLabel(x, lang)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("tf.pphAmount")}</Label>
                    <Input type="number" value={form.pph_amount} onChange={(e) => set("pph_amount", e.target.value)} data-testid="txn-pph-amount" className="num" />
                  </div>
                  {form.pph_type_id !== "none" && (
                    <div className={full}>
                      <KopSelect tax={taxes.find((x) => x.id === form.pph_type_id)} value={form.kop} onChange={(v) => set("kop", v)} lang={lang}
                        text={`${form.description} ${partyName} ${form.lines.map((l) => `${l.ledger || ""} ${l.note || ""}`).join(" ")}`} />
                    </div>
                  )}
                  {mode !== "contra" && ((Number(form.ppn_amount) || 0) > 0 || (Number(form.pph_amount) || 0) > 0) && (
                    <div className={full}><TaxIdReminder clientId={clientId} name={partyName} lang={lang} /></div>
                  )}
                </>
              )}
              <div className={`${full} rounded-md border border-border bg-secondary/40 px-3 py-2 flex items-center justify-between`}>
                <span className="text-sm text-muted-foreground">{t("tf.total")}</span>
                <span className="num font-semibold" data-testid="txn-total">{fmtRp(idrBase + (isSales ? 0 : (Number(form.ppn_amount) || 0) - (Number(form.pph_amount) || 0)), lang)}</span>
              </div>
              <div className={`${full} space-y-1.5`}>
                <Label>{t("tf.desc")}</Label>
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="txn-desc" rows={2} />
              </div>
              <div className={`${full} space-y-1.5`}>
                <Label>{t("tf.attachment")}</Label>
                <Input key={fileKey} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv" onChange={(e) => setAttachFile(e.target.files?.[0] || null)} data-testid="txn-attachment" />
                {editTxn && <p className="text-[11px] text-muted-foreground">{tx("oldAttach", lang)}</p>}
              </div>
              <div className={`${full} flex items-center justify-between rounded-md border border-border p-3`}>
                <div>
                  <Label>{t("tf.pending")}</Label>
                  <p className="text-xs text-muted-foreground">{t("tf.pendingHint")}</p>
                </div>
                <Switch checked={form.pending_document} onCheckedChange={(v) => set("pending_document", v)} data-testid="txn-pending" />
              </div>
            </>
          )}
        </div>
        {mode !== "journal" && (
          <DialogFooter className="sm:justify-between">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-xs text-muted-foreground hidden sm:block" data-testid="txn-kbd-hint">{fl("kbdHint", lang)}</span>
              {!editTxn && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <Switch checked={keepOpen} onCheckedChange={setKeepOpen} data-testid="txn-keep-open" />
                  {t("tf.keepOpen")}
                </label>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
              <Button onClick={submit} disabled={saving} data-save="" data-testid="txn-save">{saving ? t("tf.saving") : t("common.save")}</Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
